import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IncompleteRecord } from '@his/shared';
import { get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateOnly, humanize } from '@/lib/utils';
import { DataTable, Dialog, FilterBar, FilterField, PageHeader, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface RecordRequest {
  id: string;
  requestedBy: string;
  requesterType: string;
  purpose: string;
  consentRef: string | null;
  status: string;
  createdAt: string;
  releasedAt: string | null;
  patient: { id: string; mrn: string; fullName: string } | null;
}

const KIND_LABEL: Record<string, string> = {
  discharge_summary: 'mrd_kind_discharge_summary',
  operative_note: 'mrd_kind_operative_note',
  coding: 'mrd_kind_coding',
};

/**
 * Medical Records (SRS 10.3).
 *
 * The worklist is sorted oldest-first and shows the age in days, because an
 * unfinished record is only a problem in proportion to how long it has been
 * unfinished — and that is also the number the coder quotes when chasing a
 * consultant.
 */
const MRD_TABS = ['worklist', 'requests', 'retention'];

export function MrdPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab = requested && MRD_TABS.includes(requested) ? requested : 'worklist';
  const setTab = (id: string) => setParams(id === 'worklist' ? {} : { tab: id }, { replace: true });

  return (
    <>
      <PageHeader
        title={tr('admin', 'mrd_title')}
        description={tr('admin', 'mrd_description')}
        actions={
          tab === 'requests' && (
            <Button variant="outline" onClick={() => navigate('/mrd/requests/new')}>
              {tr('admin', 'mrd_log_request')}
            </Button>
          )
        }
      />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'worklist', label: tr('admin', 'mrd_tab_worklist') },
          { id: 'requests', label: tr('admin', 'mrd_tab_requests') },
          { id: 'retention', label: tr('admin', 'mrd_tab_retention') },
        ]}
      />
      {tab === 'worklist' && <WorklistTab />}
      {tab === 'requests' && <RequestsTab />}
      {tab === 'retention' && <RetentionTab />}
    </>
  );
}

function WorklistTab() {
  const { can } = useAuth();
  const [coding, setCoding] = useState<IncompleteRecord | null>(null);

  const worklist = useQuery({
    queryKey: ['mrd-worklist'],
    queryFn: () => get<IncompleteRecord[]>('/mrd/worklist'),
  });

  const rows = worklist.data ?? [];
  const over7 = rows.filter((r) => r.ageDays > 7);

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <StatTile label={tr('admin', 'mrd_unfinished_records')} value={rows.length} />
        <StatTile
          label={tr('admin', 'mrd_unsigned_summaries')}
          value={rows.filter((r) => r.kind === 'discharge_summary').length}
          tone="warning"
        />
        <StatTile label={tr('admin', 'mrd_uncoded_episodes')} value={rows.filter((r) => r.kind === 'coding').length} />
        <StatTile label={tr('admin', 'mrd_older_than_week')} value={over7.length} tone={over7.length ? 'destructive' : 'default'} />
      </div>

      <DataTable
        rows={rows}
        loading={worklist.isLoading} refreshing={worklist.isFetching}
        error={worklist.error}
        rowKey={(row) => `${row.kind}-${row.id}`}
        empty={tr('admin', 'mrd_nothing_outstanding')}
        columns={[
          {
            header: tr('admin', 'mrd_issue'),
            sort: (row) => row.kind,
            cell: (row) => <Badge variant="secondary">{KIND_LABEL[row.kind] ? tr('admin', KIND_LABEL[row.kind]) : humanize(row.kind)}</Badge>,
          },
          { header: tr('admin', 'mrd_reference'), sort: (row) => row.reference, cell: (row) => <span className="font-mono text-xs">{row.reference}</span> },
          {
            header: tr('admin', 'mrd_patient'),
            sort: (row) => row.patientName,
            cell: (row) => (
              <div>
                <p>{row.patientName}</p>
                <p className="text-xs text-muted-foreground">{row.mrn}</p>
              </div>
            ),
          },
          { header: tr('admin', 'mrd_responsible'), sort: (row) => row.responsibleName, cell: (row) => row.responsibleName ?? '—' },
          { header: tr('admin', 'closed'), sort: (row) => row.closedAt, cell: (row) => dateOnly(row.closedAt) },
          {
            header: tr('admin', 'mrd_age'),
            align: 'right',
            sort: (row) => row.ageDays,
            cell: (row) => (
              <span className={cn('tabular', row.ageDays > 7 && 'font-semibold text-destructive')}>{tr('admin', 'mrd_age_days', { days: row.ageDays })}</span>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.kind === 'coding' &&
              can('mrd.code') && (
                <Button size="sm" onClick={() => setCoding(row)}>
                  {tr('admin', 'code')}
                </Button>
              ),
          },
        ]}
      />

      {coding !== null && (
        <CodingDialog record={coding} onClose={() => setCoding(null)} />
      )}
    </>
  );
}

function CodingDialog({ record, onClose }: { record: IncompleteRecord | null; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState<{ code: string; title: string; isPrimary: boolean }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const results = useQuery({
    queryKey: ['icd', term],
    queryFn: () => get<{ code: string; title: string }[]>(`/mrd/icd${qs({ q: term })}`),
    enabled: term.trim().length >= 2,
  });

  const save = useMutation({
    mutationFn: () =>
      post('/mrd/coding', {
        encounterId: record?.id,
        diagnoses: picked.map((p) => ({ icdCode: p.code, description: p.title, isPrimary: p.isPrimary })),
      }),
    onSuccess: () => {
      toast.success(tr('admin', 'mrd_episode_coded'));
      void queryClient.invalidateQueries({ queryKey: ['mrd-worklist'] });
      setPicked([]);
      setTerm('');
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open={Boolean(record)}
      onClose={onClose}
      wide
      title={tr('admin', 'mrd_icd_coding')}
      description={record ? `${record.patientName} · ${record.mrn}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('admin', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={picked.length === 0}
          >
            {tr('admin', 'mrd_save_coding')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert>{tr('admin', 'mrd_coding_replaces')}</Alert>

        <Field label={tr('admin', 'mrd_search_icd')}>
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={tr('admin', 'mrd_search_icd_placeholder')} />
        </Field>

        {(results.data?.length ?? 0) > 0 && (
          <div className="max-h-52 overflow-y-auto rounded-md border">
            {results.data?.map((icd) => (
              <button
                key={icd.code}
                type="button"
                className="flex w-full items-baseline gap-2 border-b px-3 py-2 text-start text-sm last:border-0 hover:bg-muted focus-ring"
                onClick={() =>
                  setPicked((current) =>
                    current.some((p) => p.code === icd.code)
                      ? current
                      : [...current, { code: icd.code, title: icd.title, isPrimary: current.length === 0 }],
                  )
                }
              >
                <span className="font-mono text-xs font-semibold">{icd.code}</span>
                <span>{icd.title}</span>
              </button>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          {picked.map((p) => (
            <div key={p.code} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
              <div>
                <span className="font-mono text-xs font-semibold">{p.code}</span> {p.title}
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="radio"
                    name="primary"
                    checked={p.isPrimary}
                    onChange={() =>
                      setPicked((current) => current.map((c) => ({ ...c, isPrimary: c.code === p.code })))
                    }
                  />
                  {tr('opd', 'dx_primary')}
                </label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPicked((current) => current.filter((c) => c.code !== p.code))}
                >
                  {tr('admin', 'remove')}
                </Button>
              </div>
            </div>
          ))}
          {picked.length === 0 && <p className="text-sm text-muted-foreground">{tr('admin', 'mrd_nothing_selected')}</p>}
        </div>

        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function RequestsTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [releasing, setReleasing] = useState<RecordRequest | null>(null);
  const [consentRef, setConsentRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  const requests = useQuery({
    queryKey: ['record-requests', status],
    queryFn: () => get<RecordRequest[]>(`/mrd/requests${qs({ status: status || undefined, limit: 200 })}`),
  });

  const decide = useMutation({
    mutationFn: (body: { status: string; consentRef?: string }) => put(`/mrd/requests/${releasing?.id}`, body),
    onSuccess: () => {
      toast.success(tr('admin', 'mrd_request_updated'));
      void queryClient.invalidateQueries({ queryKey: ['record-requests'] });
      setReleasing(null);
      setConsentRef('');
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <>
      <FilterBar>
        <FilterField label={tr('admin', 'status')}>
          <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{tr('admin', 'mrd_all')}</option>
            {['pending', 'approved', 'released', 'rejected'].map((s) => (
              <option key={s} value={s}>
                {humanize(s)}
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      <DataTable
        rows={requests.data}
        loading={requests.isLoading} refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => row.id}
        empty={tr('admin', 'mrd_no_requests')}
        columns={[
          { header: tr('admin', 'mrd_logged'), sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
          {
            header: tr('admin', 'mrd_patient'),
            sort: (row) => row.patient?.fullName,
            cell: (row) =>
              row.patient ? (
                <div>
                  <p>{row.patient.fullName}</p>
                  <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
                </div>
              ) : (
                '—'
              ),
          },
          { header: tr('admin', 'mrd_requested_by'), sort: (row) => row.requestedBy, cell: (row) => row.requestedBy },
          {
            header: tr('admin', 'mrd_as'),
            sort: (row) => row.requesterType,
            cell: (row) => <Badge variant="secondary">{humanize(row.requesterType, 'requester_type')}</Badge>,
          },
          { header: tr('admin', 'mrd_purpose'), sort: (row) => row.purpose, cell: (row) => row.purpose },
          {
            header: tr('admin', 'mrd_consent'),
            sort: (row) => row.consentRef,
            cell: (row) => row.consentRef ?? <span className="text-destructive">{tr('admin', 'mrd_none')}</span>,
          },
          { header: tr('admin', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              !['released', 'rejected'].includes(row.status) &&
              can('mrd.release') && (
                <Button size="sm" onClick={() => setReleasing(row)}>
                  {tr('admin', 'mrd_release')}
                </Button>
              ),
          },
        ]}
      />

      <Dialog
        open={Boolean(releasing)}
        onClose={() => setReleasing(null)}
        title={tr('admin', 'mrd_release_title')}
        description={tr('admin', 'mrd_release_description')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setReleasing(null)}>
              {tr('admin', 'cancel')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setError(null);
                decide.mutate({ status: 'rejected' });
              }}
            >
              {tr('admin', 'mrd_refuse')}
            </Button>
            <Button
              onClick={() => {
                setError(null);
                decide.mutate({ status: 'released', consentRef: consentRef || undefined });
              }}
              loading={decide.isPending}
              disabled={!consentRef && !releasing?.consentRef}
            >
              {tr('admin', 'mrd_release')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={tr('admin', 'mrd_consent_reference')} required hint={tr('admin', 'mrd_consent_reference_hint')}>
            <Input
              value={consentRef || releasing?.consentRef || ''}
              onChange={(e) => setConsentRef(e.target.value)}
            />
          </Field>
          {error && <Alert variant="destructive">{error}</Alert>}
        </div>
      </Dialog>
    </>
  );
}

function RetentionTab() {
  const retention = useQuery({
    queryKey: ['retention'],
    queryFn: () =>
      get<{
        retentionYears: number;
        cutoff: string;
        admissionsPastRetention: number;
        encountersPastRetention: number;
        oldestRecordAt: string | null;
        note: string;
      }>('/mrd/retention'),
  });

  const r = retention.data;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatTile label={tr('admin', 'mrd_retention_policy')} value={r ? tr('admin', 'mrd_retention_years', { years: r.retentionYears }) : '—'} />
        <StatTile label={tr('admin', 'mrd_cutoff')} value={r ? dateOnly(r.cutoff) : '—'} />
        <StatTile label={tr('admin', 'mrd_admissions_past_retention')} value={r?.admissionsPastRetention ?? 0} />
        <StatTile label={tr('admin', 'mrd_episodes_past_retention')} value={r?.encountersPastRetention ?? 0} />
      </div>
      {r && (
        <Alert>
          {r.note}
          {r.oldestRecordAt && ` ${tr('admin', 'mrd_oldest_record', { date: dateOnly(r.oldestRecordAt) })}`}
        </Alert>
      )}
    </div>
  );
}
